import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Theme,
  makeStyles,
  createStyles
} from '@material-ui/core';
import { Folder as FolderIcon } from '@material-ui/icons';

import { FileDto } from '../Document.interface';
import { useMoveFile } from '../Document.hook';
import DocumentFileExplorer from './DocumentFileExplorer';
import AppSnackbar, { AppSnackbarProps } from '../../../common/AppSnackbar';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    primary: {
      backgroundColor: theme.palette.primary.main
    },
    list: {
      padding: 0,
      marginTop: theme.spacing(2),
      backgroundColor: theme.palette.grey[100]
    }
  })
);

interface SnackbarProps {
  message?: string;
  severity?: AppSnackbarProps['severity'];
}

export interface DocumentFileExplorerDialogProps {
  open: boolean;
  filesToMove?: number[];
  onCancel: () => void;
  onMoveSuccess?: () => void;
}

const DocumentFileExplorerDialog: React.FC<DocumentFileExplorerDialogProps> = (
  props: DocumentFileExplorerDialogProps
) => {
  const classes = useStyles();
  const { open, filesToMove, onCancel, onMoveSuccess } = props;
  const [{ data, isLoading, error }, put] = useMoveFile();
  const [checkReplace, setCheckReplace] = useState<boolean>(false);
  const [destination, setDestination] = useState<FileDto | undefined>();
  const [snackbarProps, setSnackbarProps] = useState<SnackbarProps>({});
  const isDestinationValid: boolean = !!(
    destination &&
    (destination.id > 0 || destination.id === -1)
  );

  // reset state on toggle dialog
  useEffect(() => {
    setCheckReplace(false);
    setDestination(undefined);
  }, [open]);

  useEffect(() => {
    if (!data) return;

    setSnackbarProps({
      severity: error ? 'error' : 'success',
      message: error ? 'Unable to move files.' : 'Moved files successfully.'
    });

    if (!error) {
      onMoveSuccess?.();
      onCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // submit files to move
    if (destination) {
      filesToMove?.forEach((id: number) =>
        put({
          id,
          destinationFolderId: destination.id,
          shouldUpdateIfExists: checkReplace
        })
      );
    }
  };

  return (
    <>
      <Dialog {...{ open }} onClose={onCancel}>
        {isLoading && <LinearProgress />}

        <form {...{ onSubmit }}>
          <DialogTitle>Move File(s)</DialogTitle>

          <DialogContent>
            <DialogContentText>Pick a folder:</DialogContentText>
            <DocumentFileExplorer
              hideTrashDir
              onSelectFile={(_, file: FileDto) => setDestination(file)}
            />

            {destination && (
              <Box boxShadow={1}>
                <List dense className={classes.list}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar className={classes.primary}>
                        <FolderIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={destination.name}
                      secondary='Selected files will be moved here.'
                    />
                  </ListItem>
                </List>
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <FormControlLabel
              label='Replace files with same name'
              control={
                <Checkbox
                  color='primary'
                  name='checkReplace'
                  disabled={isLoading}
                  checked={checkReplace}
                  onChange={() => setCheckReplace(!checkReplace)}
                />
              }
            />

            {/* divider */}
            <span style={{ flex: '1 0 0' }} />

            <Button disabled={isLoading} onClick={onCancel}>
              Close
            </Button>
            <Button
              type='submit'
              color='primary'
              variant='contained'
              disabled={isLoading || !isDestinationValid}
            >
              Move
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <AppSnackbar
        open={!!snackbarProps?.message}
        severity={snackbarProps?.severity}
        onClose={() => setSnackbarProps({ ...snackbarProps, message: '' })}
      >
        {snackbarProps?.message}
      </AppSnackbar>
    </>
  );
};

export default DocumentFileExplorerDialog;
